// ─── Backlog #3, #5 and #10: who is waiting, finding a thread, and a date range ──
//
// THERAPYLINK-CONVERSATIONS-AUDIT-2026-09-08 §4 ranks ten UX items by
// cost-of-being-missed. Items 1 and 2 shipped on 2026-09-08. Item 4 — one source
// of truth for the form-type whitelist — shipped tonight as
// therapylink-crm/src/lib/crossRepoDrift.test.js. This file is the next three
// that are self-contained: no schema change, no new endpoint, no secret.
//
//   #3  unread / needs-reply     M, no schema. "Derivable from the last message's
//                                role", which is exactly what waitingOn() does.
//   #5  search across threads    M, no schema, CLIENT-SIDE over loaded rows. The
//                                whole-table version needs a pg_trgm index; that
//                                is a different item and is not this.
//   #10 date-range filter        S, no schema. Ranked last on purpose, "after the
//                                count-and-empty-state work that makes filtering
//                                legible" — which shipped in b342b0e, so its
//                                precondition is met.
//
// PURE ON PURPOSE, same reason as ./conversationView and ./leadClassification:
// this repo has jest via react-scripts but no @testing-library, so a pure
// function is the only thing a test can actually pin.

// ─── #3 — who is waiting on whom ─────────────────────────────────────────────
//
// The Conversations tab can say what the AGENT raised (the Escalation column). It
// cannot say what a CANDIDATE said that nobody answered — which is the more
// expensive silence, because an escalation at least got written down somewhere.
//
// Roles, from recruiter-agent: 'candidate' (them), 'agent' (the bot), 'human' (a
// recruiter via /staff-reply). So the last message's role answers it:
//
//   candidate spoke last  -> US. Nobody has replied.
//   agent spoke last      -> THEM, but the agent handled it.
//   human spoke last      -> THEM, and a person handled it.
//   no messages           -> nobody; the thread has not started.

export const WAITING_ON_US = 'us';
export const WAITING_ON_THEM = 'them';
export const WAITING_NOBODY = 'nobody';

// THE SAME SET as recruiter-agent's INBOUND_ROLES (the unanswered-inbound sweep,
// src/index.js). Exported so conversationTriageDrift.test.js can hold the two repos to
// one definition: the list says "no reply" exactly where the sweep would say
// "unanswered". A declared no-reply is an AGENT row (status 'cancelled'), so it is
// answered in both.
export const CANDIDATE_ROLES = new Set(['candidate', 'user']);
const OUR_ROLES = new Set(['agent', 'human', 'assistant', 'staff']);

/**
 * The last message in a conversation, by created_at, or null.
 *
 * Ties are broken by the LAST row seen rather than the first: two messages with
 * the same timestamp are almost always a candidate turn and the reply logged in
 * the same second, and reading the reply as "not yet answered" would be the
 * expensive direction to be wrong in.
 */
export function lastMessageOf(messages, conversationId) {
  let best = null;
  let bestT = -Infinity;
  (messages || []).forEach((m) => {
    if (!m || m.conversation_id !== conversationId) return;
    const t = m.created_at ? Date.parse(m.created_at) : NaN;
    const at = Number.isNaN(t) ? -Infinity : t;
    if (at >= bestT) {
      bestT = at;
      best = m;
    }
  });
  return best;
}

/** WAITING_ON_US | WAITING_ON_THEM | WAITING_NOBODY for one conversation. */
export function waitingOn(messages, conversationId) {
  const last = lastMessageOf(messages, conversationId);
  if (!last) return WAITING_NOBODY;
  const role = (last.role ? String(last.role) : '').trim().toLowerCase();
  if (CANDIDATE_ROLES.has(role)) return WAITING_ON_US;
  if (OUR_ROLES.has(role)) return WAITING_ON_THEM;
  // An unrecognised role is NOT quietly treated as answered. A thread whose last
  // turn we cannot classify is a thread somebody should look at.
  return WAITING_ON_US;
}

/**
 * How long the candidate has been waiting, in whole hours, or null.
 *
 * Only meaningful when waitingOn() is WAITING_ON_US; returns null otherwise, so
 * a caller cannot accidentally render "waiting 400h" against a thread that was
 * answered 400 hours ago.
 */
export function waitingHours(messages, conversationId, nowMs) {
  if (waitingOn(messages, conversationId) !== WAITING_ON_US) return null;
  const last = lastMessageOf(messages, conversationId);
  const t = last && last.created_at ? Date.parse(last.created_at) : NaN;
  if (Number.isNaN(t)) return null;
  const ms = (typeof nowMs === 'number' ? nowMs : Date.now()) - t;
  return ms < 0 ? 0 : Math.floor(ms / 3600000);
}

/** The pill for the Conversations list. Never null; an unknown state says so. */
export function waitingPill(state, hours) {
  if (state === WAITING_ON_US) {
    return {
      label: hours == null ? 'Waiting on us' : `Waiting on us · ${hours}h`,
      tone: 'amber',
      title: 'The candidate spoke last and nobody has replied.',
    };
  }
  if (state === WAITING_ON_THEM) {
    return { label: 'Replied', tone: 'grey', title: 'Our side spoke last.' };
  }
  return { label: '—', tone: 'grey', title: 'No messages on this thread yet.' };
}

// ─── Waiting on us, in MINUTES, oldest first (2026-09-25) ────────────────────
//
// waitingHours() floors to whole hours, so a candidate who has waited 5, 29 or 31
// minutes all read "Waiting on us · 0h" -- the three are different jobs, and the one
// that matters most (just past half an hour) looked identical to the one that just
// arrived. These give the list minutes, one threshold, and an order.

// Past this many minutes a waiting thread carries the overdue marker. ONE constant:
// the label, the marker and the tests all read it.
export const WAITING_ALERT_MINUTES = 30;

/** Minutes the candidate has been waiting, or null (same guard as waitingHours). */
export function waitingMinutes(messages, conversationId, nowMs) {
  if (waitingOn(messages, conversationId) !== WAITING_ON_US) return null;
  const last = lastMessageOf(messages, conversationId);
  const t = last && last.created_at ? Date.parse(last.created_at) : NaN;
  if (Number.isNaN(t)) return null;
  const ms = (typeof nowMs === 'number' ? nowMs : Date.now()) - t;
  return ms < 0 ? 0 : Math.floor(ms / 60000);
}

/** Strictly older than the threshold: 30 minutes is not yet overdue, 31 is. */
export function isOverdue(minutes) {
  return typeof minutes === 'number' && minutes > WAITING_ALERT_MINUTES;
}

/**
 * The Waiting-on cell. Like waitingPill, never null. An overdue wait carries a
 * visible marker in the LABEL (not colour alone) and a red tone.
 */
export function waitingBadge(state, minutes) {
  if (state !== WAITING_ON_US) return { ...waitingPill(state), overdue: false };
  if (minutes == null) return { ...waitingPill(state, null), overdue: false };
  const overdue = isOverdue(minutes);
  return {
    label: `${overdue ? '⚠ ' : ''}Waiting on us · ${minutes} min`,
    tone: overdue ? 'red' : 'amber',
    title: overdue
      ? `The candidate has waited more than ${WAITING_ALERT_MINUTES} minutes and nobody has replied.`
      : 'The candidate spoke last and nobody has replied.',
    overdue,
  };
}

/**
 * Oldest wait first. Threads waiting on us come first, longest wait at the top; every
 * other thread keeps its existing relative order after them (the sort is stable).
 * Pure: returns a new array.
 */
export function orderOldestWaitingFirst(conversations, messages, nowMs) {
  const list = [...(conversations || [])];
  const age = new Map(list.map((cv) => [cv, waitingMinutes(messages, cv && cv.id, nowMs)]));
  return list
    .map((cv, i) => ({ cv, i }))
    .sort((a, b) => {
      const x = age.get(a.cv);
      const y = age.get(b.cv);
      if (x == null && y == null) return a.i - b.i;
      if (x == null) return 1;
      if (y == null) return -1;
      return y - x || a.i - b.i;
    })
    .map((e) => e.cv);
}

/**
 * The Leads list's indicator (2026-09-25): "no reply · N min" for a lead whose thread
 * is waiting on us, else null. Built on waitingOn/waitingMinutes, so it cannot
 * disagree with the Conversations list about which threads are waiting.
 */
export function noReplyLabel(messages, conversationId, nowMs) {
  const m = waitingMinutes(messages, conversationId, nowMs);
  return m == null ? null : `no reply · ${m} min`;
}

// ─── #5 — search, client-side, over rows already loaded ──────────────────────
//
// SCOPE, stated because the audit distinguishes them: this searches the rows the
// CRM has ALREADY loaded. It is not a table scan, it will not find a thread that
// was never fetched, and the count label has to say so or it becomes a new way to
// believe something false — which is the exact failure ./conversationView exists
// to prevent.

/** Normalise for comparison: case-folded, whitespace-collapsed. */
function norm(v) {
  return String(v == null ? '' : v).toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Does this conversation match the query, in itself or in any of its messages?
 *
 * Matches on: candidate_phone, candidate_id, channel, status, and message bodies.
 * A digits-only query also matches a phone with punctuation stripped, so
 * "5551230000" finds "+1 (555) 123-0000" — which is how a recruiter reads a
 * number off a screen.
 */
export function conversationMatches(conversation, messages, query) {
  const q = norm(query);
  if (!q) return true;
  if (!conversation) return false;

  // candidate_phone is DELIBERATELY not in this list. Matching it as a raw
  // substring would make "555" return every SMS thread in the CRM, which is a
  // search that looks like it works and is worse than none. A phone is matched
  // only through the digits path below, which requires enough digits to be a real
  // lookup rather than an accident.
  const fields = [conversation.candidate_id, conversation.channel, conversation.status];
  for (const f of fields) {
    if (norm(f).includes(q)) return true;
  }
  const digits = q.replace(/\D/g, '');
  if (digits.length >= 7) {
    const phoneDigits = String(conversation.candidate_phone || '').replace(/\D/g, '');
    if (phoneDigits && phoneDigits.includes(digits)) return true;
  }
  return (messages || []).some(
    (m) => m && m.conversation_id === conversation.id && norm(m.body).includes(q)
  );
}

/** Filter a list of conversations by query. An empty query returns the input. */
export function searchConversations(conversations, messages, query) {
  if (!norm(query)) return conversations || [];
  return (conversations || []).filter((cv) => conversationMatches(cv, messages, query));
}

/**
 * The sentence under a search result. Says what was searched, because "3 results"
 * over a partial corpus is the kind of number people trust too far.
 */
export function describeSearch(query, shown, loaded) {
  if (!norm(query)) return null;
  return `${shown} of ${loaded} loaded conversation${loaded === 1 ? '' : 's'} `
    + `match “${String(query).trim()}”. Search covers threads already loaded, `
    + 'not the whole table.';
}

// ─── #10 — date range ────────────────────────────────────────────────────────
//
// Ranked last on purpose. A date filter added before the count-and-empty-state
// work "trades a real absence for a new way to hide rows"; that work shipped in
// b342b0e, so this is now safe to add — and it follows the same rule: it must be
// able to SAY what it is hiding.

export const NO_RANGE = Object.freeze({ from: null, to: null });

/** Inclusive on both ends, day-granular. A null end means unbounded. */
export function withinRange(iso, range) {
  const r = range || NO_RANGE;
  if (!r.from && !r.to) return true;
  const t = iso ? Date.parse(iso) : NaN;
  // A row with no usable date is KEPT. Dropping it would hide a thread because of
  // a missing timestamp, which is a data problem masquerading as a filter.
  if (Number.isNaN(t)) return true;
  if (r.from) {
    const f = Date.parse(`${r.from}T00:00:00.000Z`);
    if (!Number.isNaN(f) && t < f) return false;
  }
  if (r.to) {
    const to = Date.parse(`${r.to}T23:59:59.999Z`);
    if (!Number.isNaN(to) && t > to) return false;
  }
  return true;
}

/**
 * Filter conversations by last activity, falling back to started_at — the same
 * key sortByActivity uses, so the list does not filter on one date and sort on
 * another.
 */
export function applyDateRange(conversations, activity, range) {
  const r = range || NO_RANGE;
  if (!r.from && !r.to) return conversations || [];
  return (conversations || []).filter((cv) => {
    const a = (activity && activity.get && activity.get(cv && cv.id)) || null;
    const iso = (a && a.lastAt) || (cv && cv.started_at) || null;
    return withinRange(iso, r);
  });
}

/** A human sentence naming an active range, or null when nothing is narrowed. */
export function describeRange(range) {
  const r = range || NO_RANGE;
  if (r.from && r.to) return `activity between ${r.from} and ${r.to}`;
  if (r.from) return `activity on or after ${r.from}`;
  if (r.to) return `activity on or before ${r.to}`;
  return null;
}
